import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ComposedChart, Scatter, ScatterChart, ZAxis, Treemap, TreemapItem
} from 'recharts';
import { 
  Package, Warehouse, Box, AlertTriangle, CheckCircle2, 
  TrendingUp, TrendingDown, Zap, Target, Users, DollarSign, 
  ArrowUpRight, ArrowDownRight, RefreshCw, Filter, Search,
  Calendar, Layers, Cube, Database, BarChart3, PieChart as PieChartIcon
} from 'lucide-react';

const InventoryTab = ({ timeRange }) => {
  const [selectedWarehouse, setSelectedWarehouse] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [filterCategory, setFilterCategory] = useState('all');

  // Mock data for inventory
  const inventoryData = [
    { 
      id: 'INV-001', 
      name: 'Electronics', 
      category: 'Electronics',
      quantity: 1250, 
      capacity: 2000, 
      value: 450000, 
      status: 'optimal',
      location: 'Warehouse A',
      lastUpdated: '2024-03-20 10:30',
      trend: 'up',
      items: [
        { name: 'Laptops', quantity: 500, value: 200000 },
        { name: 'Smartphones', quantity: 750, value: 250000 }
      ]
    },
    { 
      id: 'INV-002', 
      name: 'Clothing', 
      category: 'Apparel',
      quantity: 3200, 
      capacity: 5000, 
      value: 180000, 
      status: 'low',
      location: 'Warehouse B',
      lastUpdated: '2024-03-20 09:15',
      trend: 'down',
      items: [
        { name: 'T-Shirts', quantity: 2000, value: 100000 },
        { name: 'Jeans', quantity: 1200, value: 80000 }
      ]
    },
    { 
      id: 'INV-003', 
      name: 'Food Items', 
      category: 'Food',
      quantity: 800, 
      capacity: 1500, 
      value: 75000, 
      status: 'critical',
      location: 'Warehouse C',
      lastUpdated: '2024-03-20 11:45',
      trend: 'down',
      items: [
        { name: 'Canned Goods', quantity: 500, value: 45000 },
        { name: 'Fresh Produce', quantity: 300, value: 30000 }
      ]
    },
    { 
      id: 'INV-004', 
      name: 'Machinery', 
      category: 'Industrial',
      quantity: 45, 
      capacity: 100, 
      value: 1200000, 
      status: 'optimal',
      location: 'Warehouse D',
      lastUpdated: '2024-03-20 08:20',
      trend: 'up',
      items: [
        { name: 'Heavy Equipment', quantity: 25, value: 800000 },
        { name: 'Tools', quantity: 20, value: 400000 }
      ]
    }
  ];

  const warehouseMetrics = [
    { month: 'Jan', stockLevel: 85, turnover: 12, value: 450000, efficiency: 92 },
    { month: 'Feb', stockLevel: 78, turnover: 15, value: 480000, efficiency: 88 },
    { month: 'Mar', stockLevel: 82, turnover: 11, value: 420000, efficiency: 90 },
    { month: 'Apr', stockLevel: 90, turnover: 18, value: 520000, efficiency: 95 },
    { month: 'May', stockLevel: 87, turnover: 14, value: 470000, efficiency: 93 },
    { month: 'Jun', stockLevel: 94, turnover: 20, value: 580000, efficiency: 97 }
  ];

  const categoryDistribution = [
    { name: 'Electronics', value: 35, color: '#3B82F6' },
    { name: 'Apparel', value: 25, color: '#10B981' },
    { name: 'Food', value: 20, color: '#F59E0B' },
    { name: 'Industrial', value: 15, color: '#8B5CF6' },
    { name: 'Others', value: 5, color: '#EF4444' }
  ];

  const warehouses = [
    { id: 'A', name: 'Warehouse A', location: 'Riyadh', capacity: 10000, used: 7500, status: 'active' },
    { id: 'B', name: 'Warehouse B', location: 'Jeddah', capacity: 8000, used: 6000, status: 'active' },
    { id: 'C', name: 'Warehouse C', location: 'Dammam', capacity: 6000, used: 4800, status: 'warning' },
    { id: 'D', name: 'Warehouse D', location: 'Makkah', capacity: 5000, used: 4500, status: 'critical' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'optimal': return 'text-green-600 bg-green-50 border-green-200';
      case 'low': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'optimal': return <CheckCircle2 className="w-4 h-4" />;
      case 'low': return <AlertTriangle className="w-4 h-4" />;
      case 'critical': return <AlertTriangle className="w-4 h-4" />;
      default: return <Package className="w-4 h-4" />;
    }
  };

  const getWarehouseStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500';
      case 'warning': return 'bg-yellow-500';
      case 'critical': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getUtilizationPercentage = (used, capacity) => {
    return Math.round((used / capacity) * 100);
  };

  return (
    <div className="p-8 space-y-8">
      {/* Inventory Overview Header */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-3xl p-6 text-white"
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">Inventory Management</h2>
            <p className="text-purple-100">Real-time stock monitoring and predictive analytics</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm">Live Updates</span>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="p-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300"
            >
              <RefreshCw className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Quick Stats */}
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-4 gap-6"
      >
        {[
          { label: 'Total Items', value: '5,295', change: '+8.2%', icon: Package, color: 'from-blue-500 to-cyan-500' },
          { label: 'Total Value', value: 'SAR 1.9M', change: '+12.5%', icon: DollarSign, color: 'from-green-500 to-emerald-500' },
          { label: 'Low Stock Items', value: '23', change: '-5.1%', icon: AlertTriangle, color: 'from-yellow-500 to-orange-500' },
          { label: 'Warehouse Utilization', value: '84.2%', change: '+2.3%', icon: Warehouse, color: 'from-purple-500 to-pink-500' }
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 + index * 0.1, duration: 0.5 }}
            className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20 hover:bg-white/80 transition-all duration-300"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-xl bg-gradient-to-r ${stat.color}`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <div className="flex items-center text-green-600 text-sm">
                <ArrowUpRight className="w-4 h-4 mr-1" />
                {stat.change}
              </div>
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</div>
            <div className="text-gray-600 text-sm">{stat.label}</div>
          </motion.div>
        ))}
      </motion.div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Inventory Items */}
        <motion.div
          initial={{ x: -50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-white/20"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Inventory Items</h3>
              <p className="text-gray-600">Stock levels and status</p>
            </div>
            <div className="flex items-center space-x-2">
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">All Categories</option>
                <option value="Electronics">Electronics</option>
                <option value="Apparel">Apparel</option>
                <option value="Food">Food</option>
                <option value="Industrial">Industrial</option>
              </select>
            </div>
          </div>

          <div className="space-y-4">
            {inventoryData
              .filter(item => filterCategory === 'all' || item.category === filterCategory)
              .map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className={`p-4 rounded-2xl border transition-all duration-300 hover:shadow-lg ${
                  getStatusColor(item.status)
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-xl bg-white/50">
                      {getStatusIcon(item.status)}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{item.name}</h4>
                      <p className="text-sm text-gray-600">{item.location}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900">SAR {item.value.toLocaleString()}</div>
                    <div className="text-xs text-gray-500">{item.lastUpdated}</div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Stock Level</span>
                    <span className="font-medium">{item.quantity} / {item.capacity}</span>
                  </div>
                  
                  <div className="relative">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Utilization</span>
                      <span>{Math.round((item.quantity / item.capacity) * 100)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <motion.div
                        className={`h-2 rounded-full ${
                          item.status === 'optimal' ? 'bg-green-500' :
                          item.status === 'low' ? 'bg-yellow-500' :
                          'bg-red-500'
                        }`}
                        initial={{ width: 0 }}
                        animate={{ width: `${(item.quantity / item.capacity) * 100}%` }}
                        transition={{ duration: 1, delay: index * 0.1 }}
                      />
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-2">
                      {item.trend === 'up' ? (
                        <TrendingUp className="w-4 h-4 text-green-500" />
                      ) : (
                        <TrendingDown className="w-4 h-4 text-red-500" />
                      )}
                      <span className="text-xs text-gray-600">
                        {item.trend === 'up' ? 'Increasing' : 'Decreasing'}
                      </span>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      item.status === 'optimal' ? 'bg-green-100 text-green-700' :
                      item.status === 'low' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {item.status}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Warehouse Overview */}
        <motion.div
          initial={{ x: 50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-white/20"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Warehouse Overview</h3>
              <p className="text-gray-600">Capacity and utilization</p>
            </div>
            <Warehouse className="w-6 h-6 text-purple-600" />
          </div>

          <div className="space-y-4">
            {warehouses.map((warehouse, index) => (
              <motion.div
                key={warehouse.id}
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="p-4 bg-white/50 rounded-2xl border border-gray-200 hover:bg-white/80 transition-all duration-300"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${getWarehouseStatusColor(warehouse.status)}`}></div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{warehouse.name}</h4>
                      <p className="text-sm text-gray-600">{warehouse.location}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900">
                      {getUtilizationPercentage(warehouse.used, warehouse.capacity)}%
                    </div>
                    <div className="text-xs text-gray-500">
                      {warehouse.used.toLocaleString()} / {warehouse.capacity.toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="relative">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <motion.div
                      className={`h-2 rounded-full ${
                        warehouse.status === 'active' ? 'bg-green-500' :
                        warehouse.status === 'warning' ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}
                      initial={{ width: 0 }}
                      animate={{ width: `${getUtilizationPercentage(warehouse.used, warehouse.capacity)}%` }}
                      transition={{ duration: 1, delay: index * 0.1 }}
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Analytics Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Inventory Trends */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-white/20"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Inventory Trends</h3>
              <p className="text-gray-600">Monthly performance metrics</p>
            </div>
            <BarChart3 className="w-6 h-6 text-purple-600" />
          </div>

          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={warehouseMetrics}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                  border: 'none', 
                  borderRadius: '12px',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="stockLevel" 
                stackId="1" 
                stroke="#8B5CF6" 
                fill="#8B5CF6" 
                fillOpacity={0.3}
              />
              <Line 
                type="monotone" 
                dataKey="efficiency" 
                stroke="#10B981" 
                strokeWidth={3}
                dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
              />
              <Bar dataKey="turnover" fill="#F59E0B" radius={[4, 4, 0, 0]} />
            </ComposedChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Category Distribution */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-white/20"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Category Distribution</h3>
              <p className="text-gray-600">Inventory by category</p>
            </div>
            <PieChartIcon className="w-6 h-6 text-purple-600" />
          </div>

          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={categoryDistribution}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={5}
                dataKey="value"
              >
                {categoryDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                  border: 'none', 
                  borderRadius: '12px',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
                }}
              />
            </PieChart>
          </ResponsiveContainer>

          <div className="grid grid-cols-2 gap-4 mt-6">
            {categoryDistribution.map((item, index) => (
              <div key={index} className="flex items-center space-x-3">
                <div 
                  className="w-4 h-4 rounded-full" 
                  style={{ backgroundColor: item.color }}
                ></div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{item.name}</p>
                  <p className="text-xs text-gray-600">{item.value}%</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default InventoryTab; 